import { PolicyValidationResult } from '@policyvision/contracts';
import { ReactNode, useMemo, useState } from 'react';

import { SectionType } from '../types/PolicyCoverage';
import {
  ConfirmedValidationStatus,
  FrontendPolicyValidationResult,
  toFrontendValidationResult,
  ValidationContext,
} from './ValidationContext';

interface ValidationProviderProps {
  children: ReactNode;
  initialValidations?: PolicyValidationResult;
}

const DEFAULT_VALIDATIONS: FrontendPolicyValidationResult = {
  situations: [],
  amendments: [],
  missingSections: [],
};

const upsert = (
  list: ConfirmedValidationStatus[],
  identifier: string,
  validation: ConfirmedValidationStatus,
): ConfirmedValidationStatus[] => {
  const exists = list.some((v) => v.identifier === identifier);
  if (validation.status === 'NONE') {
    return list.filter((v) => v.identifier !== identifier);
  }
  if (exists) {
    return list.map((v) => (v.identifier === identifier ? validation : v));
  }
  return [...list, validation];
};

export const ValidationProvider = ({
  children,
  initialValidations,
}: ValidationProviderProps) => {
  const [validations, setValidations] =
    useState<FrontendPolicyValidationResult>(() =>
      initialValidations
        ? toFrontendValidationResult(initialValidations)
        : DEFAULT_VALIDATIONS,
    );

  const { validationsMap, allValidations } = useMemo(() => {
    const validationRecord: Map<string, ConfirmedValidationStatus> = new Map();
    for (const validation of validations.amendments)
      validationRecord.set(validation.identifier, validation);

    for (const validation of validations.situations)
      validationRecord.set(validation.identifier, validation);

    for (const validation of validations.missingSections ?? [])
      validationRecord.set(validation.identifier, validation);

    if (validations.policy)
      validationRecord.set(validations.policy.identifier, validations.policy);

    if (validations.assignment)
      validationRecord.set(
        validations.assignment.identifier,
        validations.assignment,
      );

    if (validations.financialData)
      validationRecord.set(
        validations.financialData.identifier,
        validations.financialData,
      );

    return {
      validationsMap: validationRecord,
      allValidations: [...validationRecord.values()],
    };
  }, [validations]);

  const handleValidationChange = (
    identifier: string,
    kind: SectionType,
    validation: ConfirmedValidationStatus,
  ) => {
    setValidations((prev) => {
      if (kind === 'amendment') {
        return {
          ...prev,
          amendments: upsert(prev.amendments, identifier, validation),
        };
      } else if (kind === 'warranty') {
        return {
          ...prev,
          situations: upsert(prev.situations, identifier, validation),
        };
      } else if (kind === 'missingSection') {
        return {
          ...prev,
          missingSections: upsert(
            prev.missingSections ?? [],
            identifier,
            validation,
          ),
        };
      } else if (kind === 'assignment') {
        if (validation.status === 'NONE') {
          return { ...prev, assignment: undefined };
        }
        return { ...prev, assignment: validation };
      } else if (kind === 'financialData') {
        if (validation.status === 'NONE') {
          return { ...prev, financialData: undefined };
        }
        return { ...prev, financialData: validation };
      } else if (validation.status === 'NONE') {
        return { ...prev, policy: undefined };
      } else {
        return { ...prev, policy: validation };
      }
    });
  };

  const findValidation = (identifier: string): ConfirmedValidationStatus => {
    return (
      validationsMap.get(identifier) ?? {
        identifier,
        status: 'NONE',
      }
    );
  };

  return (
    <ValidationContext.Provider
      value={{
        validations,
        allValidations,
        handleValidationChange,
        findValidation,
      }}
    >
      {children}
    </ValidationContext.Provider>
  );
};
